import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronDown, Check, Filter, Ticket as TicketIcon } from 'lucide-react';
import { ticketApi } from './api';
import type { Ticket, TicketStatus } from './api';
import StatusBadge from './StatusBadge';
import SlaPill from './SlaPill';

const STATUSES: Array<{ value: TicketStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'OPEN', label: 'Open' },
  { value: 'ACKNOWLEDGED', label: 'Acknowledged' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export default function TrackTickets() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({ status: '', q: '', raisedBy: '' });

  const [statusOpen, setStatusOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  async function loadTickets() {
    setLoading(true);
    setError(null);
    try {
      setTickets(await ticketApi.listTickets(filters));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load tickets');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setStatusOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedStatusLabel = STATUSES.find((s) => s.value === filters.status)?.label || 'All statuses';

  return (
    <div className="max-w-[1400px] mx-auto w-full px-4 py-8">
      <div className="bg-white rounded-2xl shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] border border-slate-200 overflow-hidden">

        {/* Header */}
        <div className="px-6 sm:px-8 py-6 border-b border-slate-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
            <TicketIcon size={20} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Track tickets</h2>
            <p className="text-sm text-slate-500">Search and manage your support requests</p>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 sm:px-8 bg-slate-50/50 border-b border-slate-100">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">

            {/* Search Subject */}
            <div className="md:col-span-4 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-slate-400" />
              </div>
              <input
                placeholder="Search subject or reference..."
                value={filters.q}
                onChange={(event) => setFilters({ ...filters, q: event.target.value })}
                onKeyDown={(event) => event.key === 'Enter' && loadTickets()}
                className="w-full rounded-xl border border-slate-300 pl-10 pr-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
              />
            </div>

            {/* Search Email */}
            <div className="md:col-span-3 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter size={16} className="text-slate-400" />
              </div>
              <input
                placeholder="Filter by your email"
                value={filters.raisedBy}
                onChange={(event) => setFilters({ ...filters, raisedBy: event.target.value })}
                onKeyDown={(event) => event.key === 'Enter' && loadTickets()}
                className="w-full rounded-xl border border-slate-300 pl-10 pr-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
              />
            </div>

            {/* Custom Status Dropdown */}
            <div className="md:col-span-3 relative" ref={dropdownRef}>
              <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={statusOpen}
                onClick={() => setStatusOpen((open) => !open)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") setStatusOpen(false);
                }}

                className={`!flex !w-full !items-center !justify-between !rounded-xl !border !px-4 !py-2.5 !text-left !text-sm !font-normal !normal-case !shadow-none !transition-all duration-200 focus:!outline-none ${statusOpen
                  ? "!border-blue-500 !bg-white !text-slate-800 !ring-2 !ring-blue-100"
                  : "!border-slate-300 !bg-white !text-slate-800 hover:!border-slate-400"
                  }`}
                style={{
                  backgroundColor: "#ffffff",
                  color: "#1e293b",
                  border: statusOpen
                    ? "1px solid #2563eb"
                    : "1px solid #cbd5e1",
                }}
              >
                <span className="font-medium text-slate-800">
                  {selectedStatusLabel}
                </span>
                <ChevronDown
                  size={16}
                  className={`shrink-0 text-slate-500 transition-transform duration-200 ${statusOpen ? "rotate-180" : ""
                    }`}
                />
              </button>

              {statusOpen && (
                <div
                  role="listbox"
                  className="absolute left-0 right-0 top-full z-50 mt-2 max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl shadow-slate-900/10"
                >
                  {STATUSES.map((status) => {
                    const isSelected = status.value === filters.status;
                    return (
                      <button
                        key={status.value || 'all'}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => {
                          setFilters({ ...filters, status: status.value });
                          setStatusOpen(false);
                        }}

                        className={`!flex !w-full !items-center !justify-between !gap-3 !rounded-lg !px-3 !py-2.5 !text-left !text-sm !normal-case !shadow-none !transition-colors duration-150 ${isSelected
                            ? "!bg-slate-100 !font-semibold !text-slate-900"
                            : "!bg-white !font-normal !text-slate-700 hover:!bg-slate-50 hover:!text-slate-900"
                          }`}
                        style={{
                          backgroundColor: isSelected ? "#f1f5f9" : "#ffffff",
                          color: isSelected ? "#0f172a" : "#334155",
                        }}
                      >
                        <span>{status.label}</span>
                        {isSelected && <Check size={16} className="shrink-0 text-slate-700" strokeWidth={3} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Search Button */}
            <div className="md:col-span-2">
              <button
                onClick={loadTickets}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm bg-blue-600 hover:bg-blue-700 active:scale-[0.98] transition-all duration-200"
              >
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6 sm:px-8">
          {error && (
            <div className="mb-6 p-4 rounded-xl border border-red-200 bg-red-50 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent" />
              <span className="ml-3 text-slate-500 font-medium">Loading tickets...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Reference</th>
                    <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Subject</th>
                    <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
                    <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">SLA</th>
                    <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Raised</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tickets.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-slate-500">
                        No tickets found matching your criteria.
                      </td>
                    </tr>
                  )}
                  {tickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-4 px-4 text-sm font-medium">
                        <Link to={`/tickets/view/${ticket.id}`} className="text-blue-600 hover:text-blue-800 hover:underline">
                          {ticket.reference}
                        </Link>
                      </td>
                      <td className="py-4 px-4 text-sm text-slate-800 font-medium">{ticket.subject}</td>
                      <td className="py-4 px-4 text-sm text-slate-600">{ticket.category}</td>
                      <td className="py-4 px-4 text-sm">
                        <StatusBadge status={ticket.status} />
                      </td>
                      <td className="py-4 px-4 text-sm">
                        <SlaPill sla={ticket.sla} />
                      </td>
                      <td className="py-4 px-4 text-sm text-slate-500 whitespace-nowrap">
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}